import { WAITLIST_AUTO_PROMOTE_CUTOFF_MINUTES } from "shared/constants/waitlist";
import { translateWithVars } from "shared/languages";
import { CutoffUnit } from "shared/types/enum";
import { BusinessSettings } from "shared/types/entity";
import { now, tzDate } from "shared/utils/dayjs";

/** The waitlist cutoff may be at most a week before class, in either unit. */
export const CUTOFF_UNIT_MAX: Record<CutoffUnit, number> = {
  [CutoffUnit.Hour]: 168,
  [CutoffUnit.Minute]: 7 * 24 * 60,
};

/**
 * "2 hours", "30 minutes", "1 hour" ("2 jam", "30 menit"): the number with
 * the unit the admin chose, singular or plural through the i18n `count`
 * plural keys. Used by the confirm modal's diff rows and the cancellation
 * consequence note; null (policy off) reads as a dash.
 */
export const fmtCutoff = (
  value: number | null | undefined,
  unit: CutoffUnit | string | null | undefined
): string => {
  if (value == null || unit == null) return "—";
  const key =
    unit === CutoffUnit.Hour
      ? "class_settings.unit.hour"
      : "class_settings.unit.minute";
  return `${value} ${translateWithVars(key, { count: value })}`;
};

// The ONE place a studio cutoff (value + unit) becomes minutes before class
// start on the admin, the mirror of the server's util/cutoff.ts. Every
// "waitlist closed" test on the schedule pages reads through here so the two
// pages and the server can never drift.

/** Minutes for a value + unit pair; null when either half is missing. */
export const cutoffMinutes = (
  value: number | null | undefined,
  unit: string | null | undefined
): number | null => {
  if (value == null || unit == null) return null;
  return unit === CutoffUnit.Hour ? value * 60 : value;
};

/**
 * The waitlist auto-promote cutoff of the loaded settings, as minutes. Falls
 * back to the server's derived legacy hour field when the pair is absent (an
 * older server), and to the loading-only constant before settings arrive.
 */
export const waitlistCutoffMinutesOf = (
  settings:
    | Pick<
        BusinessSettings,
        | "waitlist_auto_promote_cutoff_value"
        | "waitlist_auto_promote_cutoff_unit"
        | "waitlist_auto_promote_cutoff_hours"
      >
    | undefined
): number => {
  if (!settings) return WAITLIST_AUTO_PROMOTE_CUTOFF_MINUTES;
  const fromPair = cutoffMinutes(
    settings.waitlist_auto_promote_cutoff_value,
    settings.waitlist_auto_promote_cutoff_unit
  );
  if (fromPair != null) return fromPair;
  if (settings.waitlist_auto_promote_cutoff_hours != null) {
    return settings.waitlist_auto_promote_cutoff_hours * 60;
  }
  return WAITLIST_AUTO_PROMOTE_CUTOFF_MINUTES;
};

/**
 * Has this occurrence's waitlist closed? Mirrors the server's
 * isPastAutoPromoteCutoff (booking_v2.ts): closed once within `cutoffMinutes`
 * of class start, inclusive of the cutoff instant; 0 = open until start.
 */
export const isWaitlistClosed = (
  occurrenceTimestamp: string | Date,
  cutoffMinutes: number
): boolean =>
  !now().isBefore(
    tzDate(occurrenceTimestamp).subtract(cutoffMinutes, "minute")
  );
